/**
 * Pure presentation helpers for the forecast grid (f11.1).
 *
 * Numbers arrive as decimal strings and leave as decimal strings; `Number` is
 * used for DISPLAY formatting only, never for a value sent back.
 */

import type { ForecastGrid, ForecastLine, ForecastMoney } from '@/api/client';

import { MONTH_HEADERS } from './forecastColumns';

export const EMPTY = '—';

const DECIMAL_INPUT = /^-?\d+(\.\d+)?$/;

/** Whether a typed value is a decimal the server will parse. */
export function isDecimalInput(value: string): boolean {
  return DECIMAL_INPUT.test(value.trim());
}

/** Two decimal strings denote the same number (e.g. "6" and "6.000000"). */
export function sameDecimal(a: string | null | undefined, b: string | null | undefined): boolean {
  if (a == null || b == null || a.trim() === '' || b.trim() === '') return (a ?? '').trim() === (b ?? '').trim();
  return Number(a) === Number(b);
}

function fixed(value: string | null | undefined, digits: number): string {
  if (value == null || value === '') return EMPTY;
  const number = Number(value);
  if (!Number.isFinite(number)) return EMPTY;
  return number.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

export const formatDecimal1 = (value: string | null | undefined) => fixed(value, 1);
export const formatDecimal2 = (value: string | null | undefined) => fixed(value, 2);

/** Money with thousands separators and no decimals; zero or absent shows as the empty mark. */
export function formatMoney(money: ForecastMoney | null | undefined, { zeroAsEmpty = true } = {}): string {
  if (!money) return EMPTY;
  const number = Number(money.amount);
  if (!Number.isFinite(number) || (zeroAsEmpty && number === 0)) return EMPTY;
  return number.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

/** A currency total for a stat card, e.g. "$10,268,418". */
export function formatCurrency(money: ForecastMoney | null | undefined): string {
  if (!money) return EMPTY;
  const number = Number(money.amount);
  if (!Number.isFinite(number)) return EMPTY;
  try {
    return number.toLocaleString(undefined, {
      style: 'currency',
      currency: money.currency_code,
      maximumFractionDigits: 0,
    });
  } catch {
    return `${number.toLocaleString(undefined, { maximumFractionDigits: 0 })} ${money.currency_code}`;
  }
}

export function formatMandays(value: string | null | undefined): string {
  if (value == null) return EMPTY;
  return Number(value).toLocaleString(undefined, { maximumFractionDigits: 0 });
}

/** "Mid Aug 2026 COM": the label of the cycle closing `closingMonth`. */
export function cycleLabel(periodYear: number, closingMonth: number): string {
  const next = closingMonth % 12;
  const year = closingMonth === 12 ? periodYear + 1 : periodYear;
  return `Mid ${MONTH_HEADERS[next]} ${year} COM`;
}

/** "Jan–Jul 2026", or null when no month is closed. */
export function closedSpan(grid: ForecastGrid): string | null {
  const closed = grid.months.filter((m) => m.closed);
  if (!closed.length) return null;
  const first = closed[0]?.label ?? '';
  const last = closed[closed.length - 1]?.label ?? '';
  const span = first === last ? first : `${first}–${last}`;
  return `${span} ${grid.cycle.period_year}`;
}

const RATE_SOURCE_TEXT: Record<string, string> = {
  OVERRIDE: 'the manager’s override',
  COSMA_MONTH: 'the COSMA rate for the month',
  COSMA_ANNUAL: 'the COSMA annual average',
  MISSING: 'no rate available',
};

/** Placeholder and tooltip for an empty Sales rate: the COSMA rate of the first open month. */
export function rateHint(line: ForecastLine, grid: ForecastGrid): { placeholder: string; title: string } {
  const firstOpen = grid.months.find((m) => !m.closed)?.period_month;
  const month = line.months.find((m) => m.period_month === firstOpen) ?? line.months[line.months.length - 1];
  const result = month?.result;
  if (!result) return { placeholder: '', title: 'Not calculated yet' };
  const source = RATE_SOURCE_TEXT[result.rate_source] ?? result.rate_source;
  if (!result.effective_rate) return { placeholder: '', title: `Sales rate: ${source}` };
  return {
    placeholder: formatMoney(result.effective_rate, { zeroAsEmpty: false }),
    title: `Sales rate from ${source}`,
  };
}

export interface GridFilters {
  search: string;
  client: string;
  status: string;
  product: string;
}

export const NO_FILTERS: GridFilters = { search: '', client: '', status: '', product: '' };

export function filterLines(lines: readonly ForecastLine[], filters: GridFilters): ForecastLine[] {
  const needle = filters.search.trim().toLowerCase();
  return lines.filter((line) => {
    // f11.4 (Q-f11.4-3): keyed on the line's client id, not the Kimble account string.
    if (filters.client && line.client_id !== filters.client) return false;
    if (filters.status && line.status?.code !== filters.status) return false;
    if (filters.product && line.product !== filters.product) return false;
    if (!needle) return true;
    return [line.element_code, line.account_name, line.opportunity_name, line.program_name]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(needle));
  });
}

/**
 * The toolbar's client options, one per `client_id` (f11.4). A client whose
 * Kimble account names differ (a SAP or currency suffix) is ONE option,
 * labelled with its shortest account name -- the unsuffixed spelling.
 */
export function clientOptions(lines: readonly ForecastLine[]): { id: string; label: string }[] {
  const labels = new Map<string, string>();
  for (const line of lines) {
    if (!line.client_id) continue;
    const name = line.account_name ?? '';
    const current = labels.get(line.client_id);
    if (current === undefined || (name && (!current || name.length < current.length))) {
      labels.set(line.client_id, name);
    }
  }
  return [...labels]
    .map(([id, label]) => ({ id, label: label || id }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

export function distinct(values: readonly (string | null | undefined)[]): string[] {
  return [...new Set(values.filter((v): v is string => Boolean(v)))].sort((a, b) => a.localeCompare(b));
}

/** Refetch while a recalculation is waiting or any row predates its last edit. */
export function needsPolling(grid: ForecastGrid | undefined): boolean {
  if (!grid) return false;
  return grid.recalculation.pending || grid.lines.some((line) => line.stale);
}
